export type TrackingConsentStatus = 'granted' | 'denied';

export interface TrackingConsentRecord {
  status: TrackingConsentStatus;
  version: string;
  updatedAt: string;
}

export const PRIVACY_POLICY_VERSION = '2026-06-28';
export const TERMS_VERSION = '2026-06-28';
export const OFFICIAL_RULES_VERSION = '2026-06-28';
export const TRACKING_CONSENT_VERSION = '2026-06-28';

export const ACCOUNT_TERMS_CONSENT_TEXT =
  'I am at least 13 years old, agree to the Terms of Service and applicable challenge rules, and acknowledge the Privacy Policy.';

export const EMAIL_UPDATES_CONSENT_TEXT =
  'Send me leaderboard, World Cup Blitz, upset reminder, final result, and WC26 waitlist updates. I can opt out later.';

export const SMS_CONSENT_TEXT =
  'Text me major bracket alerts and World Cup Blitz updates. Consent is optional and not required to enter. Message and data rates may apply. Reply STOP to opt out.';

const TRACKING_CONSENT_KEY = 'bb_tracking_consent_v1';
const TRACKING_CONSENT_EVENT = 'bb-tracking-consent-changed';

function getStorage(): Storage | null {
  if (typeof localStorage !== 'undefined') return localStorage;
  if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
    return window.localStorage;
  }
  return null;
}

function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === 'undefined') return false;
  return Boolean((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl);
}

export function getTrackingConsent(): TrackingConsentRecord | null {
  if (hasGlobalPrivacyControl()) {
    return {
      status: 'denied',
      version: TRACKING_CONSENT_VERSION,
      updatedAt: 'global-privacy-control',
    };
  }

  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(TRACKING_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TrackingConsentRecord;
    if (
      (parsed.status === 'granted' || parsed.status === 'denied') &&
      parsed.version === TRACKING_CONSENT_VERSION
    ) {
      return parsed;
    }
  } catch {
    storage.removeItem(TRACKING_CONSENT_KEY);
  }

  return null;
}

export function canUseNonEssentialTracking(): boolean {
  return getTrackingConsent()?.status === 'granted';
}

export function setTrackingConsent(status: TrackingConsentStatus): TrackingConsentRecord {
  const storage = getStorage();
  const record: TrackingConsentRecord = {
    status,
    version: TRACKING_CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };

  if (storage) {
    storage.setItem(TRACKING_CONSENT_KEY, JSON.stringify(record));
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TRACKING_CONSENT_EVENT, { detail: record }));
  }

  return record;
}

export function onTrackingConsentChange(callback: (record: TrackingConsentRecord | null) => void) {
  if (typeof window === 'undefined') return () => {};

  const handler = () => callback(getTrackingConsent());
  window.addEventListener(TRACKING_CONSENT_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(TRACKING_CONSENT_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}
